<template>
  <!--
    Phone drawer (design 1h): ☰ / the tab bar's More opens the whole sidebar as a left sheet —
    office (with Switch), search, Pinned, More apps, account rows, credits, Settings + Log out.
    "Edit" (Admin / Super Admin only) shows big ≡ handles to drag and +/− to pin.

    Presentational: it never mutates props, never calls the store or the router, never reads
    $route. AppShell owns the arrangement and its save pipeline; this component only EMITS (see
    the script header). Drag uses vuedraggable over LOCAL copies of the id arrays, re-synced from
    props when not dragging (the AppSidebar pattern); a drop emits ONE `reorder` with full arrays.
  -->
  <div
    class="app-drawer"
    :class="{ 'app-shell-drawer--open': open, 'app-drawer--editing': isEditing }"
    data-test="app-shell-drawer-panel"
  >
    <transition name="app-drawer-fade">
      <div
        v-if="open"
        class="app-drawer__scrim"
        data-test="app-drawer-scrim"
        aria-hidden="true"
        @click="close"
      />
    </transition>

    <transition name="app-drawer-slide">
      <div
        v-if="open"
        ref="sheet"
        class="app-drawer__sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        tabindex="-1"
      >
        <!-- header: logo + wordmark + close -->
        <div class="app-drawer__head">
          <svg class="app-drawer__logo" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
            <rect width="8" height="8" x="3" y="3" rx="2" fill="#0c0f0e" stroke="#0c0f0e" />
            <path d="M7 11v4a2 2 0 0 0 2 2h4" stroke="#0c0f0e" />
            <rect width="8" height="8" x="13" y="13" rx="2" fill="#16a583" stroke="#16a583" />
          </svg>
          <span class="app-drawer__wordmark">teamio</span>
          <button
            type="button"
            class="app-drawer__icon-btn"
            aria-label="Close menu"
            data-test="app-drawer-close"
            @click="close"
          ><AppIcon name="x" :size="20" /></button>
        </div>

        <!-- account / office card -->
        <div class="app-drawer__card" data-test="app-drawer-account">
          <div class="app-drawer__who">
            <span class="app-drawer__avatar" aria-hidden="true">
              <img v-if="avatarSrc" :src="avatarSrc" alt="" @error="avatarFailed = true" />
              <template v-else>{{ initials }}</template>
            </span>
            <span class="app-drawer__who-text">
              <span class="app-drawer__name">{{ accountName }}</span>
              <span v-if="roleLabel" class="app-drawer__role">{{ roleLabel }}</span>
            </span>
          </div>
          <div v-if="officeName" class="app-drawer__office" data-test="app-drawer-office">
            <span class="app-drawer__office-icon"><AppIcon name="building" :size="16" /></span>
            <span class="app-drawer__office-text">
              <span class="app-drawer__office-name">{{ officeName }}</span>
              <span v-if="officeSub" class="app-drawer__office-sub">{{ officeSub }}</span>
            </span>
            <button
              v-if="canSwitch"
              type="button"
              class="app-drawer__switch"
              data-test="app-drawer-switch-site"
              @click="$emit('switch-site')"
            >Switch</button>
          </div>
        </div>

        <!-- search: a button — AppShell opens the full-screen search and focuses it in this tap -->
        <div class="app-drawer__search-wrap">
          <button
            type="button"
            class="app-drawer__search"
            data-test="app-drawer-search"
            @click="$emit('search')"
          >
            <AppIcon name="search" :size="16" />
            <span>Search patients, tasks or pages</span>
          </button>
        </div>

        <div class="app-drawer__scroll">
          <!-- PINNED -->
          <div class="app-drawer__section-head">
            <span class="app-drawer__label">Pinned</span>
            <button
              v-if="canEdit"
              type="button"
              class="app-drawer__edit"
              :data-test="isEditing ? 'app-drawer-done' : 'app-drawer-edit'"
              :aria-pressed="isEditing ? 'true' : 'false'"
              @click="toggleEdit"
            >{{ isEditing ? 'Done' : 'Edit' }}</button>
          </div>
          <div v-if="isEditing" class="app-drawer__hint" data-test="app-drawer-hint">
            Drag ≡ to reorder. Your first four show in the tab bar.
          </div>

          <draggable
            class="app-drawer__list"
            tag="div"
            data-test="app-drawer-pinned"
            v-bind="dragOptions"
            :value="localPinned"
            @start="onDragStart"
            @input="onPinnedInput"
            @end="onDragEnd"
          >
            <div
              v-for="(row, i) in pinnedRows"
              :key="row.id"
              class="app-drawer__row"
              :class="{ 'is-active': row.id === activeId && !isEditing }"
              :data-test="`app-drawer-item-${row.id}`"
              :data-id="row.id"
              @click="onRowClick(row)"
            >
              <button
                v-if="isEditing"
                type="button"
                class="app-drawer__toggle app-drawer__toggle--unpin"
                :data-test="`app-drawer-pin-${row.id}`"
                :aria-label="`Unpin ${row.label}`"
                @click.stop="$emit('unpin', row.id)"
              ><AppIcon name="minusC" :size="22" /></button>
              <component
                :is="rowTag(row)"
                class="app-drawer__link"
                v-bind="rowAttrs(row)"
              >
                <span class="app-drawer__row-icon"><AppIcon :name="row.icon" :size="20" /></span>
                <span class="app-drawer__row-label">{{ row.label }}</span>
                <span v-if="isEditing && i < tabBarSize" class="app-drawer__tabchip" data-test="app-drawer-tabchip">Tab bar</span>
                <template v-if="!isEditing">
                  <span v-if="countText(row)" class="app-drawer__count" data-test="app-drawer-count">{{ countText(row) }}</span>
                  <span v-else-if="row.dot" class="app-drawer__dot" data-test="app-drawer-dot" />
                </template>
              </component>
              <span
                v-if="isEditing"
                class="app-drawer-handle"
                :data-test="`app-drawer-handle-${row.id}`"
                :aria-label="`Drag to reorder ${row.label}`"
                role="img"
              ><AppIcon name="menu" :size="20" /></span>
            </div>
            <template #footer>
              <div v-if="!pinnedRows.length" class="app-drawer__empty" data-test="app-drawer-pinned-empty">
                {{ isEditing ? 'Tap + below to pin an app' : 'No pinned apps' }}
              </div>
            </template>
          </draggable>

          <!-- MORE APPS -->
          <div class="app-drawer__section-head app-drawer__section-head--more">
            <span class="app-drawer__label">More apps</span>
          </div>
          <draggable
            class="app-drawer__list"
            tag="div"
            data-test="app-drawer-more"
            v-bind="dragOptions"
            :value="localMore"
            @start="onDragStart"
            @input="onMoreInput"
            @end="onDragEnd"
          >
            <div
              v-for="row in moreRows"
              :key="row.id"
              class="app-drawer__row"
              :class="{ 'is-active': row.id === activeId && !isEditing }"
              :data-test="`app-drawer-item-${row.id}`"
              :data-id="row.id"
              @click="onRowClick(row)"
            >
              <button
                v-if="isEditing"
                type="button"
                class="app-drawer__toggle app-drawer__toggle--pin"
                :data-test="`app-drawer-pin-${row.id}`"
                :aria-label="`Pin ${row.label}`"
                @click.stop="$emit('pin', row.id)"
              ><AppIcon name="plusC" :size="22" /></button>
              <component
                :is="rowTag(row)"
                class="app-drawer__link"
                v-bind="rowAttrs(row)"
              >
                <span class="app-drawer__row-icon"><AppIcon :name="row.icon" :size="20" /></span>
                <span class="app-drawer__row-label">{{ row.label }}</span>
                <template v-if="!isEditing">
                  <span v-if="countText(row)" class="app-drawer__count" data-test="app-drawer-count">{{ countText(row) }}</span>
                  <span v-else-if="row.dot" class="app-drawer__dot" data-test="app-drawer-dot" />
                </template>
              </component>
              <span
                v-if="isEditing"
                class="app-drawer-handle"
                :data-test="`app-drawer-handle-${row.id}`"
                :aria-label="`Drag to reorder ${row.label}`"
                role="img"
              ><AppIcon name="menu" :size="20" /></span>
            </div>
          </draggable>

          <button
            v-if="isEditing && !isDefault"
            type="button"
            class="app-drawer__reset"
            data-test="app-drawer-reset"
            @click="$emit('reset-layout')"
          >Reset to default</button>

          <!-- account rows (gated like AppAccountMenu) -->
          <div v-if="canMyAccount || superAdmin || officeSwitchVisible" class="app-drawer__rows">
            <button
              v-if="canMyAccount"
              type="button"
              class="app-drawer__menu-row"
              data-test="app-drawer-my-account"
              @click="$emit('my-account')"
            >
              <span class="app-drawer__row-icon"><AppIcon name="user" :size="18" /></span>
              My account
            </button>
            <button
              v-if="superAdmin"
              type="button"
              class="app-drawer__menu-row"
              role="switch"
              :aria-checked="maskOn ? 'true' : 'false'"
              data-test="app-drawer-demo-mask"
              @click="$emit('toggle-mask')"
            >
              <span class="app-drawer__row-icon"><AppIcon name="eyeOff" :size="18" /></span>
              <span class="app-drawer__mask-text">
                <span>Demo mask</span>
                <span class="app-drawer__mask-hint">{{ maskOn ? 'Patient data is faked. Sends are blocked.' : 'Fake patient data for recording' }}</span>
              </span>
              <span class="app-drawer__switch-ui" :class="{ 'is-on': maskOn }" aria-hidden="true"><span></span></span>
            </button>
            <button
              v-if="superAdmin"
              type="button"
              class="app-drawer__menu-row"
              data-test="app-drawer-classic-nav"
              @click="$emit('classic-nav')"
            >
              <span class="app-drawer__row-icon"><AppIcon name="panelOpen" :size="18" /></span>
              Classic navigation
            </button>
            <!-- Office users (never a Super Admin): the ONE "classic Teamio" / "new Teamio" switch. -->
            <button
              v-if="officeSwitchVisible"
              type="button"
              class="app-drawer__menu-row"
              data-test="app-drawer-office-switch"
              @click="$emit('office-switch', officeSwitch.target)"
            >
              <span class="app-drawer__row-icon"><AppIcon name="panelOpen" :size="18" /></span>
              {{ officeSwitchText }}
            </button>
          </div>
        </div>

        <!-- footer (always reachable): credits, Settings, Log out -->
        <div class="app-drawer__foot">
          <CreditBalancePill pill-id="credit-balance-pill-drawer" class="app-drawer__credits" />
          <div class="app-drawer__foot-row">
            <button
              v-if="canSettings"
              type="button"
              class="app-drawer__foot-btn"
              data-test="app-drawer-settings"
              @click="$emit('settings')"
            >
              <AppIcon name="gear" :size="16" />
              Settings
            </button>
            <button
              type="button"
              class="app-drawer__foot-btn"
              data-test="app-drawer-logout"
              @click="$emit('logout')"
            >
              <AppIcon name="logout" :size="16" />
              Log out
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * App shell — phone drawer (design 1h). Plan ~/.claude/plans/nested-conjuring-flurry.md §3 / §5 / §6.
 *
 *   props  open, items (resolved rows { id, label, shortLabel, icon, to, count?, dot? }), pinned,
 *          more (ids, in order), activeId, account { name, role, avatarUrl? }, office { name,
 *          index, count }, canEdit (Admin / Super Admin), isDefault, canMyAccount, canSettings,
 *          canSwitch, superAdmin, maskOn, officeSwitch ({ visible, target } — the office users'
 *          "classic Teamio" switch, uiVersion.js officeSwitchState; hidden unless told otherwise)
 *   emits  close, search, select(item), reorder({ pinned, more }), pin(id), unpin(id),
 *          reset-layout, my-account, settings, switch-site, toggle-mask, logout, classic-nav,
 *          office-switch(target)
 *
 * While open: class `app-shell-drawer--open` on the root (agent-panel.css hides the agent button),
 * body scroll locked (restored on close / destroy), Escape closes, focus moves into the sheet and
 * back to whatever opened it (the ☰ / More tab) on close — unless something outside the drawer
 * already took focus (the full-screen search the drawer's search button hands off to).
 *
 * Edit mode is local (closing the drawer leaves it). Drag starts ONLY from the ≡ handle, so no
 * press-and-hold delay; one Sortable group spans Pinned and More.
 *
 * Prop names avoid src/mixin.js computeds (`user`, `isSuperAdmin`, `siteID`, `patNum`, `loading`) —
 * cypress/node-tests/mixin-prop-shadow-guardrail.test.js.
 */
import draggable from 'vuedraggable';
import AppIcon from './AppIcon.vue';
import { countLabel } from './AppNavItem.vue';
import CreditBalancePill from '@/components/CreditBalancePill.vue';
import { TAB_BAR_SIZE } from '@/utils/nav/layout';
import { officeSwitchLabel } from '@/utils/followups/uiCopy.js';

/** Its own Sortable group (the sidebar is never mounted beside the drawer). */
export const DRAWER_DRAG_GROUP = 'app-shell-drawer';

function sameIds(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

export default {
  name: 'AppDrawer',
  components: { draggable, AppIcon, CreditBalancePill },
  props: {
    open: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    pinned: { type: Array, default: () => [] },
    more: { type: Array, default: () => [] },
    activeId: { type: String, default: null },
    account: { type: Object, default: () => ({}) },
    office: { type: Object, default: () => ({}) },
    canEdit: { type: Boolean, default: false },
    isDefault: { type: Boolean, default: true },
    canMyAccount: { type: Boolean, default: false },
    canSettings: { type: Boolean, default: false },
    canSwitch: { type: Boolean, default: false },
    superAdmin: { type: Boolean, default: false },
    maskOn: { type: Boolean, default: false },
    officeSwitch: { type: Object, default: () => ({ visible: false, target: null }) },
  },
  data() {
    return {
      editing: false,
      localPinned: [],
      localMore: [],
      dragging: false,
      avatarFailed: false,
    };
  },
  computed: {
    tabBarSize() {
      return TAB_BAR_SIZE;
    },
    officeSwitchVisible() {
      const s = this.officeSwitch;
      return !!(s && s.visible === true && (s.target === 'old' || s.target === 'new'));
    },
    officeSwitchText() {
      return officeSwitchLabel(this.officeSwitch && this.officeSwitch.target);
    },
    isEditing() {
      return this.editing && this.canEdit;
    },
    byId() {
      const map = {};
      this.items.forEach(row => {
        if (row && row.id) map[row.id] = row;
      });
      return map;
    },
    propPinned() {
      return this.knownIds(this.pinned);
    },
    propMore() {
      return this.knownIds(this.more);
    },
    pinnedRows() {
      return this.localPinned.map(id => this.byId[id]).filter(Boolean);
    },
    moreRows() {
      return this.localMore.map(id => this.byId[id]).filter(Boolean);
    },
    dragOptions() {
      // Bound (not bare attrs): vuedraggable forwards them camelized to Sortable.
      return {
        group: DRAWER_DRAG_GROUP,
        draggable: '.app-drawer__row',
        handle: '.app-drawer-handle',
        disabled: !this.isEditing,
        delay: 0,
        animation: 150,
        ghostClass: 'app-drawer-ghost',
        chosenClass: 'app-drawer-chosen',
      };
    },
    accountName() {
      return (this.account && this.account.name) || '';
    },
    roleLabel() {
      const r = String((this.account && this.account.role) || '').trim();
      return r ? r.charAt(0).toUpperCase() + r.slice(1).toLowerCase() : '';
    },
    initials() {
      const parts = this.accountName.split(/\s+/).filter(Boolean);
      if (!parts.length) return '';
      const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
      return (parts[0].charAt(0) + last).toUpperCase();
    },
    avatarSrc() {
      return !this.avatarFailed && this.account && this.account.avatarUrl ? this.account.avatarUrl : '';
    },
    officeName() {
      return (this.office && this.office.name) || '';
    },
    officeSub() {
      const n = Number(this.office && this.office.count) || 0;
      const i = Number(this.office && this.office.index) || 0;
      if (n <= 1) return '';
      return i > 0 ? `${i} of ${n} offices` : `${n} offices`;
    },
  },
  watch: {
    propPinned: { handler: 'syncFromProps', immediate: true },
    propMore: 'syncFromProps',
    open: { handler: 'onOpenChange', immediate: true },
    canEdit(value) {
      if (!value) this.editing = false;
    },
    'account.avatarUrl'() {
      this.avatarFailed = false;
    },
  },
  // Non-reactive handles. beforeCreate, not created: the immediate `open` watcher runs before
  // created() and may already have locked the body.
  beforeCreate() {
    this.returnFocusTo = null;
    this.lockedBody = false;
    this.prevBodyOverflow = '';
    this.keyListening = false;
  },
  beforeDestroy() {
    this.unlockBody();
    this.removeKeyListener();
  },
  methods: {
    knownIds(list) {
      const seen = {};
      return (Array.isArray(list) ? list : []).filter(id => {
        if (!this.byId[id] || seen[id]) return false;
        seen[id] = true;
        return true;
      });
    },
    syncFromProps() {
      if (this.dragging) return;
      this.localPinned = this.propPinned.slice();
      this.localMore = this.propMore.slice();
    },
    countText(row) {
      return countLabel(row.count);
    },
    // Edit mode: rows stop navigating (a plain div); otherwise a router-link (it navigates itself).
    rowTag(row) {
      return !this.isEditing && row.to ? 'router-link' : 'div';
    },
    rowAttrs(row) {
      if (this.isEditing || !row.to) return {};
      return { to: row.to, 'aria-current': row.id === this.activeId ? 'page' : null, draggable: 'false' };
    },
    // The click bubbles from the link to the row; the −/+ buttons stop their own.
    onRowClick(row) {
      if (this.isEditing) return;
      this.$emit('select', row);
    },
    toggleEdit() {
      this.editing = !this.editing;
    },
    close() {
      this.$emit('close');
    },

    // ---- open / close side effects --------------------------------------------------------------
    onOpenChange(isOpen, wasOpen) {
      if (isOpen) {
        this.returnFocusTo = typeof document !== 'undefined' ? document.activeElement : null;
        this.lockBody();
        this.addKeyListener();
        this.$nextTick(this.focusSheet);
        return;
      }
      this.editing = false;
      this.unlockBody();
      this.removeKeyListener();
      if (wasOpen) this.restoreFocus();
    },
    focusSheet() {
      const sheet = this.$refs.sheet;
      if (!this.open || !sheet || typeof sheet.focus !== 'function') return;
      try { sheet.focus({ preventScroll: true }); } catch (e) { sheet.focus(); }
    },
    /** Hand focus back only if it is still inside the drawer (or nowhere) — never steal it. */
    restoreFocus() {
      if (typeof document === 'undefined') return;
      const target = this.returnFocusTo;
      this.returnFocusTo = null;
      const active = document.activeElement;
      const inside = active && this.$el && typeof this.$el.contains === 'function' && this.$el.contains(active);
      if (!inside && active && active !== document.body) return;
      if (target && target !== document.body && typeof target.focus === 'function' && document.contains(target)) {
        try { target.focus(); } catch (e) { /* detached */ }
      } else if (inside && typeof active.blur === 'function') {
        active.blur();
      }
    },
    lockBody() {
      if (this.lockedBody || typeof document === 'undefined' || !document.body) return;
      this.prevBodyOverflow = document.body.style.overflow || '';
      document.body.style.overflow = 'hidden';
      this.lockedBody = true;
    },
    unlockBody() {
      if (!this.lockedBody || typeof document === 'undefined' || !document.body) return;
      document.body.style.overflow = this.prevBodyOverflow;
      this.prevBodyOverflow = '';
      this.lockedBody = false;
    },
    addKeyListener() {
      if (this.keyListening || typeof document === 'undefined') return;
      document.addEventListener('keydown', this.onKeydown);
      this.keyListening = true;
    },
    removeKeyListener() {
      if (!this.keyListening || typeof document === 'undefined') return;
      document.removeEventListener('keydown', this.onKeydown);
      this.keyListening = false;
    },
    onKeydown(e) {
      if (!this.open || !e) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        // A dialog opened FROM the drawer (credits popover, a b-modal) closes first.
        if (typeof document !== 'undefined' && document.body && document.body.classList.contains('modal-open')) return;
        this.close();
      }
    },

    // ---- drag (AppSidebar pattern) ----------------------------------------------------------------
    onDragStart() {
      this.dragging = true;
    },
    onPinnedInput(ids) {
      this.localPinned = ids.slice();
    },
    onMoreInput(ids) {
      this.localMore = ids.slice();
    },
    /**
     * Sortable fires add / remove (→ @input, synchronous) before end, so both local lists hold the
     * drop. Emit ONE reorder when they differ from the props, then fall back to the props (the shell
     * applies the change optimistically; if it refuses, the old order comes back).
     */
    onDragEnd() {
      this.dragging = false;
      const pinned = this.localPinned.slice();
      const more = this.localMore.slice();
      if (!sameIds(pinned, this.propPinned) || !sameIds(more, this.propMore)) {
        this.$emit('reorder', { pinned, more });
      }
      this.$nextTick(this.syncFromProps);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './shell';

.app-drawer__scrim {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: $shell-z-drawer;
  background: rgba(12, 15, 14, 0.32);
}

.app-drawer__sheet {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: $shell-z-drawer;
  width: $shell-drawer-width;
  max-width: $shell-drawer-max-vw;
  display: flex;
  flex-direction: column;
  background: $fu-surface;
  box-shadow: $fu-shadow-xl;
  font-family: $fu-font-sans;
  color: $fu-text;
  text-align: left;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  outline: none;
  @media print { display: none; }
}

// ---- transitions (design: nvFade .18s, nvSlide .22s ease-out) ----------------------------------
.app-drawer-fade-enter-active,
.app-drawer-fade-leave-active { transition: opacity $fu-dur-base ease; }
.app-drawer-fade-enter,
.app-drawer-fade-leave-to { opacity: 0; }
.app-drawer-slide-enter-active,
.app-drawer-slide-leave-active { transition: transform 220ms $fu-ease-out; }
.app-drawer-slide-enter,
.app-drawer-slide-leave-to { transform: translateX(-100%); }
@media (prefers-reduced-motion: reduce) {
  .app-drawer-fade-enter-active,
  .app-drawer-fade-leave-active,
  .app-drawer-slide-enter-active,
  .app-drawer-slide-leave-active { transition: none; }
}

// ---- header -------------------------------------------------------------------------------------
.app-drawer__head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 6px 6px 16px;
}
.app-drawer__logo { display: block; flex: none; }
.app-drawer__wordmark {
  @include fu-font-display;
  flex: 1;
  font-weight: 800;
  font-size: 19px;
  letter-spacing: -0.02em;
  color: $fu-text-strong;
}
.app-drawer__icon-btn {
  @include fu-icon-btn($shell-touch-target);
  color: $fu-neutral-500;
}

// ---- account / office card ----------------------------------------------------------------------
.app-drawer__card {
  flex: none;
  margin: 2px 16px 8px;
  border: 1px solid $fu-border;
  border-radius: 12px;
  background: $fu-neutral-25;
}
.app-drawer__who {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
}
.app-drawer__avatar {
  width: 36px;
  height: 36px;
  flex: none;
  border-radius: 50%;
  overflow: hidden;
  background: $fu-brand-50;
  color: $fu-brand-700;
  font-size: 13px;
  font-weight: $fu-fw-bold;
  display: flex;
  align-items: center;
  justify-content: center;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.app-drawer__who-text,
.app-drawer__office-text,
.app-drawer__mask-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
}
.app-drawer__name { font-size: 15px; font-weight: $fu-fw-bold; @include fu-ellipsis; }
.app-drawer__role { font-size: 12.5px; color: $fu-neutral-500; }
.app-drawer__office {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 4px 4px 12px;
  border-top: 1px solid $fu-border;
}
.app-drawer__office-icon { display: flex; color: $fu-neutral-500; }
.app-drawer__office-text { flex: 1; }
.app-drawer__office-name { font-size: 14px; font-weight: $fu-fw-semibold; @include fu-ellipsis; }
.app-drawer__office-sub { font-size: 12px; color: $fu-neutral-500; }
.app-drawer__switch {
  @include fu-button-reset;
  @include fu-focus-ring;
  flex: none;
  min-height: $shell-touch-target;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: $fu-fw-bold;
  color: $fu-brand-700;
}

// ---- search button --------------------------------------------------------------------------------
.app-drawer__search-wrap { flex: none; padding: 0 16px 6px; }
.app-drawer__search {
  @include fu-button-reset;
  @include fu-focus-ring;
  width: 100%;
  height: $shell-touch-target;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  border: 1px solid $fu-border;
  border-radius: 12px;
  background: $fu-neutral-50;
  font-size: 15px;
  color: $fu-neutral-500;
  text-align: left;
  cursor: text;
}

// ---- lists ----------------------------------------------------------------------------------------
.app-drawer__scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 2px 10px 12px;
}
.app-drawer__section-head {
  display: flex;
  align-items: center;
  padding: 10px 4px 4px 12px;
  &--more { padding-top: 16px; }
}
.app-drawer__label {
  font-size: 11px;
  font-weight: $fu-fw-bold;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: $fu-neutral-500;
}
.app-drawer__edit {
  @include fu-button-reset;
  @include fu-focus-ring;
  margin-left: auto;
  min-height: $shell-touch-target;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: $fu-fw-bold;
  color: $fu-brand-700;
}
.app-drawer__hint {
  padding: 0 12px 8px;
  font-size: 13px;
  line-height: 1.45;
  color: $fu-neutral-500;
}
.app-drawer__list {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-height: 48px;
}
.app-drawer__empty {
  padding: 12px;
  font-size: 13px;
  color: $fu-neutral-500;
}
.app-drawer__row {
  display: flex;
  align-items: center;
  min-height: 48px;
  border-radius: 10px;
  &.is-active { background: $fu-neutral-100; }
}
.app-drawer__link {
  flex: 1;
  min-width: 0;
  min-height: 48px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 4px 0 12px;
  border-radius: 10px;
  color: $fu-text;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  &:hover,
  &:focus { color: $fu-text; text-decoration: none; }
  &:focus-visible { outline: none; box-shadow: $fu-ring; }
}
.app-drawer__row-icon { display: flex; flex: none; color: $fu-neutral-500; }
.app-drawer__row-label {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: $fu-fw-medium;
  @include fu-ellipsis;
}
.app-drawer__row.is-active {
  .app-drawer__row-icon { color: $fu-brand; }
  .app-drawer__row-label { font-weight: $fu-fw-bold; }
}
.app-drawer__count {
  padding-right: 10px;
  font-size: 13px;
  font-weight: $fu-fw-bold;
  color: $fu-neutral-500;
  @include fu-tabular;
}
.app-drawer__dot {
  flex: none;
  width: 8px;
  height: 8px;
  margin-right: 12px;
  border-radius: 50%;
  background: $fu-brand-500;
}
.app-drawer__tabchip {
  flex: none;
  padding: 2px 7px;
  border-radius: 999px;
  background: $fu-brand-50;
  color: $fu-brand-700;
  font-size: 11px;
  font-weight: $fu-fw-bold;
}

// Edit mode: −/+ in front, ≡ handle behind (design hEdit), both 44×44.
.app-drawer__toggle {
  @include fu-button-reset;
  @include fu-focus-ring;
  flex: none;
  width: $shell-touch-target;
  height: $shell-touch-target;
  margin-right: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  &--unpin { color: $fu-danger; }
  &--pin { color: $fu-brand; }
}
.app-drawer-handle {
  flex: none;
  width: $shell-touch-target;
  height: $shell-touch-target;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $fu-neutral-400;
  cursor: grab;
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
}
.app-drawer--editing .app-drawer__link { cursor: default; }

.app-drawer__reset {
  @include fu-button-reset;
  @include fu-focus-ring;
  min-height: $shell-touch-target;
  margin: 6px 0 0 4px;
  padding: 0 8px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: $fu-fw-semibold;
  color: $fu-brand-700;
}

// ---- account rows -----------------------------------------------------------------------------------
.app-drawer__rows {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid $fu-border;
}
.app-drawer__menu-row {
  @include fu-button-reset;
  @include fu-focus-ring;
  width: 100%;
  min-height: $shell-touch-target;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 4px 6px 12px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: $fu-fw-medium;
  color: $fu-text;
  text-align: left;
  &:hover { background: $fu-neutral-50; }
}
.app-drawer__mask-text { flex: 1; }
.app-drawer__mask-hint { font-size: 12px; line-height: 1.35; color: $fu-neutral-500; }
.app-drawer__switch-ui {
  position: relative;
  width: 40px;
  height: 23px;
  flex: none;
  margin-right: 8px;
  border-radius: 999px;
  background: $fu-neutral-300;
  span {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 19px;
    height: 19px;
    border-radius: 50%;
    background: #fff;
    box-shadow: $fu-shadow-sm;
    transition: transform $fu-dur-fast $fu-ease-out;
  }
  &.is-on {
    background: $fu-brand;
    span { transform: translateX(17px); }
  }
}

// ---- footer -----------------------------------------------------------------------------------------
.app-drawer__foot {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 16px 16px;
  border-top: 1px solid $fu-border;
}
.app-drawer__credits { display: flex; }
.app-drawer__foot-row { display: flex; gap: 8px; }
.app-drawer__foot-btn {
  @include fu-button-reset;
  @include fu-focus-ring;
  flex: 1;
  height: $shell-touch-target;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid $fu-border;
  border-radius: 10px;
  font-size: 14px;
  font-weight: $fu-fw-semibold;
  color: $fu-text;
  ::v-deep .app-icon { color: $fu-neutral-500; }
  &:hover { background: $fu-neutral-50; }
}
</style>

<style lang="scss">
// Drag ghost / chosen row (Sortable adds these classes; scoped styles can't target them reliably).
.app-drawer-ghost { opacity: 0.35; }
.app-drawer-chosen { background: #fff; box-shadow: 0 8px 16px rgba(12, 15, 14, 0.06), 0 24px 48px rgba(12, 15, 14, 0.12); }

// The credits popover (b-popover, Bootstrap z 1060, portaled to <body>) must open ABOVE the drawer.
body:has(.app-shell-drawer--open) .credit-pill-popover.popover { z-index: 10002; }
</style>
